import * as THREE from 'three';
import type { GLTF } from 'three/addons/loaders/GLTFLoader.js';
import {
  DT,
  FREE_CONTROL,
  STEP_HEIGHT,
  UNIT_HALF_HEIGHT,
  horizontal,
  length,
  moveStep,
  sub,
  v3,
  yawFromDirection,
  type CollisionWorld,
  type MoveState,
  type V3,
} from '@arena/shared';

/** Kenney Cube Pets, by path under /models. */
export const PETS = [
  { model: 'pets/animal-dog', name: 'Argos' },
  { model: 'pets/animal-cat', name: 'Loco' },
  { model: 'pets/animal-bunny', name: 'Biggie Cheese' },
  { model: 'pets/animal-chick', name: 'Diesel' },
  { model: 'pets/animal-fox', name: 'Volpe' },
  { model: 'pets/animal-penguin', name: 'King' },
  { model: 'pets/animal-pig', name: 'Escalade' },
  { model: 'pets/animal-koala', name: 'Reg' },
];

const PET_SCALE = 0.45;
const CONTROL = { ...FREE_CONTROL, speed: 0.3 };
/** Critters spawn in a ring around the hub's central platform and wander within ROAM of the center. */
const SPAWN_RADIUS = [8, 13];
const ROAM = 16;
const GIVE_UP_SECONDS = 8;
const GOAL_TRIES = 6;

interface Critter {
  root: THREE.Object3D;
  hitbox: THREE.Mesh;
  nameplate: HTMLDivElement;
  /** Above the feet, in world units. */
  nameplateHeight: number;
  mixer: THREE.AnimationMixer;
  actions: Map<string, THREE.AnimationAction>;
  playing: THREE.AnimationAction | null;
  /** Which half of the walk loop it's in, null when not walking. */
  stride: number | null;
  move: MoveState;
  previous: MoveState;
  yaw: number;
  goal: V3 | null;
  until: number;
}

/** Ambient pets that wander the hub. Client-only: every player sees their own. */
export class Critters {
  private critters: Critter[] = [];
  /** Each footstep, with the critter's root at its feet. */
  onFootfall: ((at: THREE.Object3D) => void) | null = null;
  private accumulator = 0;
  private time = 0;

  constructor(
    scene: THREE.Scene,
    private world: CollisionWorld,
    models: GLTF[],
    overlay: HTMLElement,
  ) {
    models.forEach((model, i) => {
      const root = model.scene.clone();
      const bounds = new THREE.Box3().setFromObject(root);
      const hitbox = new THREE.Mesh(
        new THREE.BoxGeometry(...bounds.getSize(new THREE.Vector3()).toArray()),
        new THREE.MeshBasicMaterial({ visible: false }),
      );
      bounds.getCenter(hitbox.position);
      root.add(hitbox);
      root.scale.setScalar(PET_SCALE);
      scene.add(root);
      const nameplate = document.createElement('div');
      nameplate.className = 'nameplate critter';
      nameplate.style.display = 'none';
      const label = document.createElement('div');
      label.className = 'np-name';
      label.textContent = PETS[i].name;
      nameplate.append(label);
      overlay.append(nameplate);
      const mixer = new THREE.AnimationMixer(root);
      const actions = new Map(
        model.animations.map((clip) => [clip.name, mixer.clipAction(clip)]),
      );
      const angle = Math.random() * Math.PI * 2;
      const radius =
        SPAWN_RADIUS[0] + Math.random() * (SPAWN_RADIUS[1] - SPAWN_RADIUS[0]);
      const move: MoveState = {
        pos: v3(
          Math.cos(angle) * radius,
          UNIT_HALF_HEIGHT + 0.1,
          Math.sin(angle) * radius,
        ),
        vel: v3(),
        grounded: false,
        groundNormal: v3(0, 1, 0),
        yaw: Math.random() * Math.PI * 2,
      };
      const critter: Critter = {
        root,
        hitbox,
        nameplate,
        nameplateHeight: bounds.max.y * PET_SCALE + 0.3,
        mixer,
        actions,
        playing: null,
        stride: null,
        move,
        previous: move,
        yaw: move.yaw,
        goal: null,
        until: Math.random() * 4,
      };
      this.play(critter, 'idle');
      this.critters.push(critter);
    });
  }

  /** Shows the nameplate of the critter under the mouse, if any; critters are never targetable. */
  label(
    ray: THREE.Raycaster | null,
    camera: THREE.Camera,
    width: number,
    height: number,
  ): void {
    const hit = ray?.intersectObjects(
      this.critters.map((c) => c.hitbox),
      false,
    )[0];
    const projected = new THREE.Vector3();
    for (const c of this.critters) {
      const shown = c.hitbox === hit?.object;
      c.nameplate.style.display = shown ? '' : 'none';
      if (!shown) continue;
      projected
        .copy(c.root.position)
        .setY(c.root.position.y + c.nameplateHeight)
        .project(camera);
      c.nameplate.style.transform = `translate(${((projected.x + 1) / 2) * width}px, ${((1 - projected.y) / 2) * height}px) translate(-50%, -100%)`;
    }
  }

  dispose(): void {
    for (const c of this.critters) c.nameplate.remove();
  }

  update(dt: number): void {
    this.time += dt;
    this.accumulator = Math.min(this.accumulator + dt, DT * 5);
    while (this.accumulator >= DT) {
      for (const critter of this.critters) this.step(critter);
      this.accumulator -= DT;
    }
    const alpha = this.accumulator / DT;
    for (const c of this.critters) {
      const { previous: a, move: b } = c;
      c.root.position.set(
        a.pos.x + (b.pos.x - a.pos.x) * alpha,
        a.pos.y + (b.pos.y - a.pos.y) * alpha - UNIT_HALF_HEIGHT,
        a.pos.z + (b.pos.z - a.pos.z) * alpha,
      );
      const turn = Math.atan2(Math.sin(b.yaw - c.yaw), Math.cos(b.yaw - c.yaw));
      c.yaw += turn * Math.min(1, dt * 8);
      // Kenney models face +z; yaw 0 faces -z.
      c.root.rotation.y = c.yaw + Math.PI;
      c.mixer.update(dt);
      this.footfalls(c);
    }
  }

  /** Like UnitView's: feet strike a quarter and three quarters of the way through the walk loop. */
  private footfalls(c: Critter): void {
    const walk = c.actions.get('walk');
    if (!walk || c.playing !== walk) {
      c.stride = null;
      return;
    }
    const stride =
      Math.floor((walk.time / walk.getClip().duration) * 2 + 0.5) % 2;
    if (c.stride !== null && stride !== c.stride) this.onFootfall?.(c.root);
    c.stride = stride;
  }

  private step(c: Critter): void {
    if (!c.goal && this.time >= c.until) {
      c.goal = this.clearGoal(c.move.pos);
      c.until = this.time + (c.goal ? GIVE_UP_SECONDS : 1);
      if (c.goal) this.play(c, 'walk');
    }
    const to = c.goal ? horizontal(sub(c.goal, c.move.pos)) : v3();
    if (c.goal && (length(to) < 0.5 || this.time >= c.until)) {
      c.goal = null;
      c.until = this.time + 2 + Math.random() * 5;
      this.play(c, Math.random() < 0.3 ? 'eat' : 'idle');
    }
    c.previous = c.move;
    c.move = moveStep(
      this.world,
      c.move,
      {
        seq: 0,
        forward: c.goal ? 1 : 0,
        strafe: 0,
        yaw: c.goal ? yawFromDirection(to) : c.move.yaw,
        jump: false,
      },
      CONTROL,
    );
  }

  /** A random point within ROAM the capsule can walk straight to; raised a step so stairs and gentle slopes don't block. */
  private clearGoal(from: V3): V3 | null {
    const raised = v3(from.x, from.y + STEP_HEIGHT, from.z);
    for (let i = 0; i < GOAL_TRIES; i++) {
      const goal = v3(
        (Math.random() * 2 - 1) * ROAM,
        0,
        (Math.random() * 2 - 1) * ROAM,
      );
      if (!this.world.castCapsule(raised, horizontal(sub(goal, from)), 0))
        return goal;
    }
    return null;
  }

  private play(c: Critter, name: string): void {
    const action = c.actions.get(name);
    if (!action || action === c.playing) return;
    action.reset().fadeIn(0.2).play();
    c.playing?.fadeOut(0.2);
    c.playing = action;
  }
}
